/**
 * YUMIX — Extra Miles Hero Badges.
 *
 * Two reusable components that surface the EXTRA MILES superhero mascots
 * across the app — they're not just landing-page branding anymore.
 *
 *   • <HeroMaleCelebrate /> — pops in briefly (1.8s) on a successful
 *     analysis with compliance_status='ok'. Hero pointing finger up,
 *     conveying "Excellent choice!"
 *   • <HeroFemaleWatcher /> — small persistent illustration meant for
 *     Dashboard / Profile, with the "Tes coachs veillent sur toi" tag.
 *
 * Both are pure presentation, zero side-effects. Mobile-friendly sizes.
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { playCelebrationCue } from '../utils/celebrationSound';

/**
 * Brief celebratory pop-in of the male superhero.
 *  - `show` (bool): drives the AnimatePresence
 *  - `language` ('fr' | 'en')
 *  - `onDone` (fn): optional callback when the timer auto-hides
 *  - `duration` (ms): default 1800
 */
export const HeroMaleCelebrate = ({ show, language = 'fr', onDone, duration = 1800 }) => {
  const fr = (language || 'fr').startsWith('fr');
  React.useEffect(() => {
    if (!show) return;
    // 🎵 Soft celebratory cue — auto-muted via localStorage flag if user disables it.
    playCelebrationCue();
    const t = setTimeout(() => { onDone && onDone(); }, duration);
    return () => clearTimeout(t);
  }, [show, duration, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="hero-male-celebrate"
          initial={{ opacity: 0, y: 30, scale: 0.85 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
          className="pointer-events-none fixed bottom-24 right-4 z-[60] flex items-end gap-2"
          data-testid="hero-male-celebrate"
        >
          <div className="relative">
            {/* Pulse glow */}
            <motion.div
              aria-hidden
              className="absolute inset-0 rounded-full"
              style={{ background: "radial-gradient(circle, rgba(251,146,60,0.45), transparent 70%)", filter: "blur(20px)" }}
              animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
              transition={{ duration: 1.2, repeat: Infinity }}
            />
            <img
              src="/hero-male.png"
              alt={fr ? "Coach Extra Miles te félicite" : "Extra Miles coach congratulates you"}
              className="relative w-24 h-auto select-none"
              style={{ filter: "drop-shadow(0 8px 20px rgba(251,146,60,0.55))" }}
              draggable={false}
            />
          </div>
          {/* Speech bubble */}
          <div className="mb-3 bg-card border border-orange-500/40 rounded-2xl rounded-bl-sm px-3 py-2 shadow-lg shadow-orange-500/20 max-w-[180px]">
            <p className="text-xs font-medium text-foreground leading-snug">
              {fr ? "🌟 Excellent choix !" : "🌟 Excellent choice!"}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {fr ? "Conforme OMS · ton coach est fier" : "WHO compliant · your coach is proud"}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Small persistent coach-duo illustration. Meant to live in a
 * sidebar / profile card with the "Tes coachs veillent sur toi" tag.
 *
 * Uses the duo image (`/extra-miles-heroes.png`, 237×512 — Yuri's image
 * with a female partner present so women feel represented) so clients see
 * a real human coach behind the AI, not a cropped half-visible silhouette.
 *
 *   <HeroFemaleWatcher language={language} />
 */
export const HeroFemaleWatcher = ({ language = 'fr', size = 120, withTag = true }) => {
  const fr = (language || 'fr').startsWith('fr');
  return (
    <div
      className="relative flex items-center gap-4"
      data-testid="hero-female-watcher"
    >
      <div className="relative flex-shrink-0" style={{ width: size }}>
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-2xl"
          style={{ background: "radial-gradient(circle, rgba(251,146,60,0.28), transparent 65%)", filter: "blur(15px)" }}
          animate={{ scale: [1, 1.06, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.img
          src="/extra-miles-heroes.png"
          alt={fr ? "Coach Extra Miles veille sur toi" : "Extra Miles coach watching over you"}
          className="relative w-full h-auto select-none rounded-xl"
          style={{ filter: "drop-shadow(0 4px 12px rgba(251,146,60,0.35))", objectFit: "contain" }}
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          draggable={false}
        />
      </div>
      {withTag && (
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[10px] uppercase tracking-[0.18em] font-heading text-orange-400">
            {fr ? "✦ Extra Miles" : "✦ Extra Miles"}
          </span>
          <span className="text-sm text-foreground font-semibold leading-tight mt-0.5">
            {fr ? "Ton coach veille sur toi" : "Your coach is watching over you"}
          </span>
          <span className="text-[11px] text-muted-foreground mt-1 leading-snug">
            {fr
              ? "Yuri + l'IA derrière ton plan."
              : "Yuri + the AI behind your plan."}
          </span>
        </div>
      )}
    </div>
  );
};
